import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Truck } from "@phosphor-icons/react";
import { CARRIERS } from "@/lib/carriers";

// Add or edit the courier + tracking number on a shipment (PO), any time after creation.
export default function TrackingDialog({ open, onOpenChange, po, onSuccess }) {
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && po) {
      setCarrier(po.carrier || "");
      setTracking(po.tracking_number || "");
    }
  }, [open, po]);

  const save = async () => {
    if (!po) return;
    setSaving(true);
    try {
      await api.post(`/purchase-orders/${po.id}/tracking`, {
        carrier,
        tracking_number: tracking.trim(),
      });
      toast.success("Tracking saved");
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tracking-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Truck size={20} /> Courier tracking</DialogTitle>
          <DialogDescription>Add or update the courier and tracking number for {po?.reference || "this shipment"}.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Courier</Label>
            <Select value={carrier} onValueChange={setCarrier}>
              <SelectTrigger data-testid="tracking-carrier"><SelectValue placeholder="Select courier…" /></SelectTrigger>
              <SelectContent>
                {CARRIERS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Tracking number</Label>
            <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="e.g. CP1234567890" className="font-mono" data-testid="tracking-number-input" />
            <p className="text-[11px] text-slate-400">Leave both blank and save to clear the tracking.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-md">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 rounded-md gap-2" data-testid="tracking-save-btn">
            <Truck size={15} /> {saving ? "Saving…" : "Save tracking"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
